import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PortalLayout from '../../components/PortalLayout';
import { registerCustomer, confirmCustomerEmail, getPublicTicketProject, errorMessage } from '../../services/customerApi';
import { useLanguage } from '../../i18n/LanguageContext';

const emptyForm = { name: '', email: '', password: '' };

const EyeIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const CheckIcon = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Punteggio 0-4 della password: lunghezza, numero (richiesto dal server), lunghezza extra, simbolo.
const passwordScore = (pw) => (pw.length >= 8 ? 1 : 0) + (/[0-9]/.test(pw) ? 1 : 0) + (pw.length >= 12 ? 1 : 0) + (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);

function PortalRegister() {
  const { t } = useLanguage();
  const { projectSlug } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [form, setForm] = useState(emptyForm);
  const [customerId, setCustomerId] = useState(null);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [project, setProject] = useState(null);

  // Il colore della coda dà il tono alla pagina, come nel resto del portale.
  useEffect(() => {
    getPublicTicketProject(projectSlug)
      .then((data) => setProject(data.project))
      .catch(() => {});
  }, [projectSlug]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await registerCustomer({ ...form, project_slug: projectSlug });
      setCustomerId(res.customer_id);
      setStep('confirm');
    } catch (err) {
      setError(errorMessage(err, t('portal.registerError')));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await confirmCustomerEmail(customerId, otp);
      navigate(`/portal/${projectSlug}/login`);
    } catch (err) {
      setError(errorMessage(err, t('portal.confirmError')));
    } finally {
      setLoading(false);
    }
  };

  const steps = (
    <ol className="pr-steps" aria-label={t('portal.registerTitle')}>
      <li className={'pr-step' + (step === 'form' ? ' pr-step-current' : ' pr-step-done')}>
        <span className="pr-step-dot">{step === 'form' ? 1 : CheckIcon}</span> {t('portal.registerStepData')}
      </li>
      <li className="pr-step-line" aria-hidden="true" />
      <li className={'pr-step' + (step === 'confirm' ? ' pr-step-current' : '')}>
        <span className="pr-step-dot">2</span> {t('portal.registerStepConfirm')}
      </li>
    </ol>
  );

  if (step === 'confirm') {
    return (
      <PortalLayout title={t('portal.confirmTitle')} subtitle={t('portal.registerSuccess')} accent={project?.color}>
        {steps}
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleConfirm} className="card">
          <div className="card-body pr-confirm">
            <span className="pr-mail-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </span>
            <p className="pr-confirm-text">{t('portal.confirmSentTo', { email: form.email })}</p>
            <div className="form-group">
              <label>{t('portal.confirmOtp')}</label>
              <input
                className="pr-otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\s/g, ''))}
                required
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder="••••••"
              />
            </div>
            <button type="submit" className="btn" disabled={loading || otp.length < 6}>
              {loading ? t('portal.confirming') : t('portal.confirmSubmit')}
            </button>
            <button type="button" className="pr-back" onClick={() => { setStep('form'); setError(''); setOtp(''); }}>
              ← {t('portal.registerEditData')}
            </button>
          </div>
        </form>
      </PortalLayout>
    );
  }

  const score = passwordScore(form.password);
  const rules = [
    { ok: form.password.length >= 8, label: t('portal.pwRuleLength') },
    { ok: /[0-9]/.test(form.password), label: t('portal.pwRuleNumber') },
  ];

  return (
    <PortalLayout title={t('portal.registerTitle')} subtitle={t('portal.registerSubtitle')} accent={project?.color}>
      {steps}
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleRegister} className="card">
        <div className="card-body">
          <ul className="pr-benefits">
            <li><span aria-hidden="true">{CheckIcon}</span> {t('portal.benefitAll')}</li>
            <li><span aria-hidden="true">{CheckIcon}</span> {t('portal.benefitStatus')}</li>
            <li><span aria-hidden="true">{CheckIcon}</span> {t('portal.benefitReply')}</li>
          </ul>
          <div className="form-group">
            <label>{t('portal.name')}</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoComplete="name" />
          </div>
          <div className="form-group">
            <label>{t('portal.email')}</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
          </div>
          <div className="form-group">
            <label>{t('portal.password')}</label>
            <div className="pr-password">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="pr-eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t('profile.hidePassword') : t('profile.showPassword')}
                aria-pressed={showPassword}
              >
                {showPassword ? EyeOffIcon : EyeIcon}
              </button>
            </div>
            <div className={`pr-meter pr-meter-${score}`} aria-hidden="true">
              <span /><span /><span /><span />
            </div>
            <ul className="pr-rules">
              {rules.map((rule) => (
                <li key={rule.label} className={rule.ok ? 'pr-rule-ok' : ''}>
                  <span className="pr-rule-dot" aria-hidden="true">{rule.ok ? CheckIcon : null}</span> {rule.label}
                </li>
              ))}
            </ul>
          </div>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? t('portal.registering') : t('portal.registerSubmit')}
          </button>
        </div>
      </form>
      <p className="portal-note">
        {t('portal.alreadyHaveAccount')} <Link to={`/portal/${projectSlug}/login`}>{t('portal.login')}</Link>
      </p>
    </PortalLayout>
  );
}

export default PortalRegister;
